import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import VideoCard from '@/components/base/VideoCard';
import { videoCategories, videos } from '@/mocks/videos';

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || '';
  const [sortBy, setSortBy] = useState<'hot' | 'new' | 'rating'>('hot');

  const activeCategory = videoCategories.find((c) => c.slug === activeTab);

  const filteredVideos = useMemo(() => {
    let result = activeTab
      ? videos.filter((v) => {
          const cat = videoCategories.find((c) => c.slug === activeTab);
          return cat ? v.categoryId === cat.id : true;
        })
      : videos;

    switch (sortBy) {
      case 'new':
        return [...result].sort((a, b) => b.year - a.year);
      case 'rating':
        return [...result].sort((a, b) => b.rating - a.rating);
      case 'hot':
      default:
        return [...result].sort((a, b) => b.views - a.views);
    }
  }, [activeTab, sortBy]);

  const handleTabChange = (slug: string) => {
    if (slug) {
      setSearchParams({ tab: slug });
    } else {
      setSearchParams({});
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      {/* WebPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: activeCategory ? `${activeCategory.name} - 豆花视频网页版` : '全部分类 - 豆花视频网页版',
            description: `豆花视频网页版${activeCategory ? activeCategory.name : '全部'}视频分类，海量高清内容免费在线观看。`,
            url: `https://www.douhuavideo.com/category${activeTab ? `?tab=${activeTab}` : ''}`,
            inLanguage: 'zh-CN',
          }),
        }}
      />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '豆花视频首页',
                item: 'https://www.douhuavideo.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: activeCategory ? activeCategory.name : '全部分类',
              },
            ],
          }),
        }}
      />

      <main className="pt-16">
        <div className="bg-background-100">
          <Breadcrumb
            items={[
              {
                label: activeCategory ? activeCategory.name : '全部分类',
              },
            ]}
          />

          <div className="w-full px-4 md:px-6 py-10">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  {activeCategory ? activeCategory.name : '全部分类'}
                </h1>
                <p className="text-sm text-foreground-600">
                  发现更多精彩视频内容
                </p>
              </div>

              {/* Category Tabs */}
              <div className="flex justify-center mb-8 overflow-x-auto pb-2">
                <div className="inline-flex bg-background-50 border border-background-200/70 rounded-full p-1">
                  <button
                    onClick={() => handleTabChange('')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeTab === ''
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-800'
                    }`}
                  >
                    全部
                  </button>
                  {videoCategories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleTabChange(cat.slug)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                        activeTab === cat.slug
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-600 hover:text-foreground-800'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sort Options */}
              <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
                <p className="text-sm text-foreground-600">
                  共找到 <strong className="text-foreground-900">{filteredVideos.length}</strong> 部视频
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500">排序：</span>
                  {[
                    { key: 'hot', label: '最热' },
                    { key: 'new', label: '最新' },
                    { key: 'rating', label: '评分' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key as typeof sortBy)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                        sortBy === opt.key
                          ? 'bg-secondary-500 text-background-50'
                          : 'bg-background-50 border border-background-200/70 text-foreground-600 hover:text-foreground-800'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Video Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {filteredVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>

              {filteredVideos.length === 0 && (
                <div className="text-center py-20">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                    <i className="ri-video-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">暂无该分类的视频内容</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}